import { useEffect } from 'react'

import { validateTenantMatch } from '@/config/tenant-site'
import { TenantSuspendedPage } from '@/shared/components/TenantSuspendedPage'
import { useAuthConfig } from '@/shared/hooks/useAuthConfig'
import {
  clearTenantSuspendedPayload,
  isTenantSuspendedStatus,
  saveTenantSuspendedPayload,
} from '@/shared/lib/tenant-suspended'

/** multi 租户模式下校验站点;停用租户展示专用联系页 */
export function TenantSiteGuard({ children }: { children: React.ReactNode }) {
  const { config, loading, error, reload } = useAuthConfig()

  const suspended = Boolean(config && isTenantSuspendedStatus(config.tenant_status))

  useEffect(() => {
    if (!suspended) {
      clearTenantSuspendedPayload()
      return
    }
    const timer = window.setInterval(() => {
      void reload().catch(() => undefined)
    }, 20_000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void reload().catch(() => undefined)
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [suspended, reload])

  if (loading) {
    return <>{children}</>
  }

  if (config && suspended) {
    saveTenantSuspendedPayload({
      tenant_status: config.tenant_status,
      tenant_name: config.tenant_name,
      support: config.support,
    })
    return <TenantSuspendedPage tenantName={config.tenant_name} support={config.support} />
  }

  const mismatch =
    validateTenantMatch(config?.tenant_code, config?.tenant_mode) ??
    (error?.includes('租户配置不一致') ? error : null)

  if (mismatch) {
    return (
      <div className="flex h-full min-h-[40vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-lg font-semibold text-red-300">租户站点配置错误</h1>
        <p className="max-w-md text-sm text-zinc-400">{mismatch}</p>
        <p className="max-w-md text-xs text-zinc-500">
          请确认本实例的 <code className="text-zinc-300">VITE_TENANT_CODE</code> 与部署域名(
          <code className="text-zinc-300">VITE_TENANT_BASE_DOMAIN</code> 或 default 门户 Host) 指向同一租户,然后重新构建或重启 dev
          server。
        </p>
      </div>
    )
  }

  return <>{children}</>
}
